import { useState, useEffect } from "react"
import InputMask from "react-input-mask"
import { Chip } from "@/components/Molecules/Chip/Chip"
import { Icon } from "@/components/Atoms/Icon"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer"
import { Button } from "@/components/ui/button"

export interface LogsFilterProps {
  search: string
  onSearchChange: (value: string) => void
  dataInicio: string
  onDataInicioChange: (value: string) => void
  dataFim: string
  onDataFimChange: (value: string) => void
  onFetch: () => void
  onClear: () => void
}

function isCompleteDate(value: string) {
  return value.replace(/\D/g, "").length === 8
}

export const LogsFilter: React.FC<LogsFilterProps> = ({
  search,
  onSearchChange,
  dataInicio,
  onDataInicioChange,
  dataFim,
  onDataFimChange,
  onFetch,
  onClear,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [draftInicio, setDraftInicio] = useState(dataInicio)
  const [draftFim, setDraftFim] = useState(dataFim)
  const [appliedInicio, setAppliedInicio] = useState(dataInicio)
  const [appliedFim, setAppliedFim] = useState(dataFim)

  const openDrawer = () => {
    setDraftInicio(dataInicio)
    setDraftFim(dataFim)
    setDrawerOpen(true)
  }

  const applyFilters = () => {
    onDataInicioChange(draftInicio)
    onDataFimChange(draftFim)
    setAppliedInicio(draftInicio)
    setAppliedFim(draftFim)
    setDrawerOpen(false)
    onFetch()
  }

  useEffect(() => {
    if (dataInicio === "" || isCompleteDate(dataInicio)) setAppliedInicio(dataInicio)
  }, [dataInicio])

  useEffect(() => {
    if (dataFim === "" || isCompleteDate(dataFim)) setAppliedFim(dataFim)
  }, [dataFim])

  const handleInicioBlur = () => {
    if (isCompleteDate(dataInicio) && isCompleteDate(dataFim)) onFetch()
  }

  const handleFimBlur = () => {
    if (isCompleteDate(dataInicio) && isCompleteDate(dataFim)) onFetch()
  }

  const hasActiveFilters = isCompleteDate(appliedInicio) || isCompleteDate(appliedFim)
  const chipLabel = `De: ${appliedInicio} Até: ${appliedFim}`

  const handleClear = () => {
    setAppliedInicio("")
    setAppliedFim("")
    onClear()
  }

  return (
    <div>
      {/* Mobile */}
      <div className="lg:hidden">
        <div className="flex items-center gap-3">
          <div className="relative flex flex-1 items-center gap-3 rounded-lg border border-border bg-black/40 px-4 text-white">
            <Icon size={14} name="search" />
            <input
              type="text"
              placeholder="Buscar nos logs..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-transparent py-2.5 focus:outline-none"
            />
          </div>
          <button
            onClick={openDrawer}
            className="relative flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-black/40"
          >
            <i
              className={`fi fi-ts-sliders-v flex items-center justify-center text-base ${hasActiveFilters ? "text-orange-500" : "text-white"}`}
            />
            {hasActiveFilters && (
              <span className="absolute -right-1 -top-1 size-3 rounded-full bg-orange-500" />
            )}
          </button>
        </div>
        {hasActiveFilters && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Chip label={chipLabel} onClose={handleClear} />
          </div>
        )}
      </div>

      {/* Desktop */}
      <div className="hidden lg:block">
        <div className="flex items-center gap-4">
          <div className="relative flex w-72 items-center gap-4 rounded-lg border border-border bg-black/40 px-4 text-white">
            <Icon size={14} name="search" />
            <input
              type="text"
              placeholder="Buscar nos logs..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-transparent py-2 focus:outline-none"
            />
          </div>

          <div
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${hasActiveFilters ? "border-orange-500 bg-orange-500/10" : "border-border bg-surface"}`}
          >
            <InputMask
              mask="99/99/9999"
              value={dataInicio}
              onChange={(e) => onDataInicioChange(e.target.value)}
              onBlur={handleInicioBlur}
            >
              {(inputProps: any) => (
                <input
                  {...inputProps}
                  placeholder="dd/mm/yyyy"
                  className={`w-24 bg-transparent text-sm focus:outline-none ${hasActiveFilters ? "text-orange-400 placeholder-orange-500/40" : "text-white placeholder-white/30"}`}
                />
              )}
            </InputMask>
            <i className={`fi fi-ts-arrow-right flex items-center text-sm ${hasActiveFilters ? "text-orange-500/60" : "text-white/40"}`} />
            <InputMask
              mask="99/99/9999"
              value={dataFim}
              onChange={(e) => onDataFimChange(e.target.value)}
              onBlur={handleFimBlur}
            >
              {(inputProps: any) => (
                <input
                  {...inputProps}
                  placeholder="dd/mm/yyyy"
                  className={`w-24 bg-transparent text-sm focus:outline-none ${hasActiveFilters ? "text-orange-400 placeholder-orange-500/40" : "text-white placeholder-white/30"}`}
                />
              )}
            </InputMask>
          </div>

          {hasActiveFilters && (
            <button
              onClick={handleClear}
              className="whitespace-nowrap text-sm text-muted-foreground transition-colors hover:text-orange-500"
            >
              Limpar filtros
            </button>
          )}
        </div>
        {hasActiveFilters && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Chip label={chipLabel} onClose={handleClear} />
          </div>
        )}
      </div>

      {/* Mobile bottom sheet */}
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent className="border-white/10 bg-surface px-4 pb-2">
          <DrawerHeader>
            <DrawerTitle className="text-white">Período</DrawerTitle>
          </DrawerHeader>

          <div className="flex flex-col gap-4 px-4 pb-2">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">De</span>
              <div className="flex items-center rounded-lg border border-border bg-black/40 px-4 py-2">
                <InputMask
                  mask="99/99/9999"
                  value={draftInicio}
                  onChange={(e) => setDraftInicio(e.target.value)}
                >
                  {(inputProps: any) => (
                    <input
                      {...inputProps}
                      placeholder="dd/mm/yyyy"
                      className="w-full bg-transparent text-sm text-white placeholder-white/30 focus:outline-none"
                    />
                  )}
                </InputMask>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Até</span>
              <div className="flex items-center rounded-lg border border-border bg-black/40 px-4 py-2">
                <InputMask
                  mask="99/99/9999"
                  value={draftFim}
                  onChange={(e) => setDraftFim(e.target.value)}
                >
                  {(inputProps: any) => (
                    <input
                      {...inputProps}
                      placeholder="dd/mm/yyyy"
                      className="w-full bg-transparent text-sm text-white placeholder-white/30 focus:outline-none"
                    />
                  )}
                </InputMask>
              </div>
            </div>
          </div>

          <DrawerFooter>
            <Button
              onClick={applyFilters}
              className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600"
            >
              Aplicar filtro
            </Button>
            <Button
              onClick={() => setDrawerOpen(false)}
              variant="ghost"
              className="w-full text-muted-foreground hover:text-white"
            >
              Cancelar
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  )
}
